import * as THREE from 'three';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry.js';
import { SDL_COLLISION_GROUPS, SDL_COLLISION_MASKS } from '../constants/physics.js';
export class SceneLoader {
    textureLoader;
    textureCache = new Map();
    gltfLoader;
    preferBaked;
    baseUrl;
    maxAnisotropy;
    constructor(options = {}) {
        this.textureLoader = options.textureLoader || new THREE.TextureLoader();
        this.gltfLoader = options.gltfLoader;
        this.preferBaked = options.preferBaked ?? true;
        this.baseUrl = options.baseUrl ?? '';
        this.maxAnisotropy = options.maxAnisotropy ?? 16; // Most GPUs support at least 16
    }
    /**
     * Resolve a URL - if relative and baseUrl is set, prefix with baseUrl
     */
    resolveUrl(url) {
        if (!url)
            return url;
        // If it's already absolute, return as-is
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
        }
        // Prefix relative URLs with baseUrl
        return this.baseUrl + url;
    }
    /**
     * Load a scene document
     * If the scene has baked data and preferBaked is true, loads the optimized version
     */
    async load(document) {
        // Check for baked scene
        const bakedData = document.settings?.bakedScene;
        if (this.preferBaked && bakedData?.enabled) {
            try {
                return await this.loadBaked(document, bakedData);
            }
            catch (err) {
                console.warn('Failed to load baked scene, falling back to unbaked:', err);
            }
        }
        // Load unbaked scene
        return this.loadUnbaked(document);
    }
    /**
     * Load the original unbaked scene (individual objects)
     */
    async loadUnbaked(document) {
        const scene = new THREE.Group();
        scene.name = document.metadata.name;
        const objects = new Map();
        const lights = new Map();
        const physicsData = new Map();
        // Load objects
        for (const obj of document.objects) {
            const threeObj = await this.createObject(obj);
            if (threeObj) {
                scene.add(threeObj);
                objects.set(obj.id, threeObj);
                if ((obj.type === 'primitive' || obj.type === 'mesh') && obj.physics?.enabled) {
                    physicsData.set(obj.id, obj.physics);
                }
                // Collider objects always have physics
                if (obj.type === 'collider' && obj.physics?.enabled) {
                    physicsData.set(obj.id, obj.physics);
                }
            }
        }
        // Load lights
        for (const light of document.lights) {
            const threeLight = this.createLight(light);
            if (threeLight) {
                scene.add(threeLight);
                lights.set(light.id, threeLight);
                // SpotLight and DirectionalLight targets must be added to scene for proper orientation
                if (threeLight instanceof THREE.SpotLight || threeLight instanceof THREE.DirectionalLight) {
                    scene.add(threeLight.target);
                }
            }
        }
        return { scene, objects, lights, physicsData, isBaked: false };
    }
    /**
     * Load a baked scene (merged geometry + lightmap)
     * Much faster for mobile - single draw call for entire scene
     */
    async loadBaked(document, bakedData) {
        const scene = new THREE.Group();
        scene.name = document.metadata.name + ' (Baked)';
        const objects = new Map();
        const lights = new Map();
        const physicsData = new Map();
        // Load merged mesh
        const meshUrl = this.resolveUrl(bakedData.mergedMeshUrl);
        const mergedMesh = await this.loadBakedMesh(meshUrl);
        if (mergedMesh) {
            scene.add(mergedMesh);
            objects.set('__merged__', mergedMesh);
        }
        // Load lightmap and apply to merged mesh
        if (bakedData.lightmapUrl && mergedMesh) {
            const lightmapUrl = this.resolveUrl(bakedData.lightmapUrl);
            const lightmap = await this.loadLightmap(lightmapUrl);
            if (lightmap && mergedMesh.material instanceof THREE.MeshBasicMaterial) {
                mergedMesh.material.lightMap = lightmap;
                mergedMesh.material.lightMapIntensity = bakedData.lightmapIntensity;
                mergedMesh.material.needsUpdate = true;
            }
        }
        // Create physics bodies from original object data
        // Physics still needs individual bodies for accurate collision
        for (const objId of bakedData.originalObjectIds) {
            const obj = document.objects.find(o => o.id === objId);
            if (obj && (obj.type === 'primitive' || obj.type === 'mesh') && obj.physics?.enabled) {
                physicsData.set(objId, obj.physics);
                // Create invisible collision mesh for physics raycasting
                const collisionMesh = await this.createObject(obj);
                if (collisionMesh) {
                    collisionMesh.visible = false;
                    collisionMesh.userData.isCollisionProxy = true;
                    scene.add(collisionMesh);
                    objects.set(objId, collisionMesh);
                }
            }
        }
        // Also include collider objects (physics-only objects not part of baked mesh)
        for (const obj of document.objects) {
            if (obj.type === 'collider' && obj.physics?.enabled) {
                physicsData.set(obj.id, obj.physics);
                // Colliders are invisible, just need to add to physicsData for GameScene
                const colliderGroup = this.createCollider(obj);
                if (colliderGroup) {
                    scene.add(colliderGroup);
                    objects.set(obj.id, colliderGroup);
                }
            }
        }
        // Load lights (still needed for dynamic objects like dice)
        // But ambient can be reduced since we have baked lighting
        for (const light of document.lights) {
            const threeLight = this.createLight(light);
            if (threeLight) {
                // Reduce ambient since baked scene has ambient baked in
                if (threeLight instanceof THREE.AmbientLight) {
                    threeLight.intensity *= 0.5;
                }
                // Disable shadow casting for directional lights (shadows are baked)
                if (threeLight instanceof THREE.DirectionalLight) {
                    threeLight.castShadow = false;
                }
                scene.add(threeLight);
                lights.set(light.id, threeLight);
            }
        }
        console.log(`📦 Loaded baked scene: ${bakedData.originalObjectIds.length} objects merged`);
        return { scene, objects, lights, physicsData, isBaked: true };
    }
    /**
     * Load the merged mesh from GLB
     */
    async loadBakedMesh(meshUrl) {
        if (!this.gltfLoader) {
            console.warn('GLTFLoader not provided, cannot load baked mesh');
            return null;
        }
        // Type assertion for GLTFLoader
        const loader = this.gltfLoader;
        try {
            const gltf = await loader.loadAsync(meshUrl);
            const mesh = gltf.scene.children[0];
            if (mesh) {
                mesh.name = 'BakedSceneMesh';
                mesh.userData.isBaked = true;
                // Replace material with MeshBasicMaterial for performance
                // Keep vertex colors if present
                const hasVertexColors = mesh.geometry.getAttribute('color') !== undefined;
                mesh.material = new THREE.MeshBasicMaterial({
                    vertexColors: hasVertexColors,
                });
                // Disable shadow receiving (shadows are baked)
                mesh.receiveShadow = false;
                mesh.castShadow = false;
            }
            return mesh;
        }
        catch (err) {
            console.error('Failed to load baked mesh:', err);
            return null;
        }
    }
    /**
     * Load lightmap texture
     */
    async loadLightmap(url) {
        return new Promise((resolve) => {
            this.textureLoader.load(url, (texture) => {
                texture.flipY = false;
                texture.colorSpace = THREE.LinearSRGBColorSpace;
                resolve(texture);
            }, undefined, (err) => {
                console.error('Failed to load lightmap:', err);
                resolve(null);
            });
        });
    }
    async createObject(obj) {
        switch (obj.type) {
            case 'primitive':
                return this.createPrimitive(obj);
            case 'mesh':
                return this.createMesh(obj);
            case 'group':
                return this.createGroup(obj);
            case 'collider':
                return this.createCollider(obj);
            default:
                console.warn(`Unknown object type: ${obj.type}`);
                return null;
        }
    }
    createPrimitive(obj) {
        const geometry = this.createGeometry(obj.primitive);
        const material = this.createMaterial(obj.material);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = obj.name;
        mesh.userData.sdlId = obj.id;
        mesh.userData.sdlType = 'primitive';
        mesh.userData.locked = obj.locked;
        mesh.position.set(...obj.position);
        mesh.rotation.set(...obj.rotation);
        mesh.scale.set(...obj.scale);
        mesh.visible = obj.visible;
        mesh.castShadow = obj.castShadow ?? true;
        mesh.receiveShadow = obj.receiveShadow ?? true;
        if (obj.userData) {
            Object.assign(mesh.userData, obj.userData);
        }
        return mesh;
    }
    createGeometry(primitive) {
        switch (primitive.shape) {
            case 'box': {
                const { width, height, depth } = primitive.parameters;
                return new THREE.BoxGeometry(width, height, depth);
            }
            case 'sphere': {
                const { radius, widthSegments = 32, heightSegments = 32 } = primitive.parameters;
                return new THREE.SphereGeometry(radius, widthSegments, heightSegments);
            }
            case 'plane': {
                const { planeWidth, planeHeight } = primitive.parameters;
                return new THREE.PlaneGeometry(planeWidth, planeHeight);
            }
            case 'cylinder': {
                const { radiusTop, radiusBottom, height, radialSegments = 32 } = primitive.parameters;
                return new THREE.CylinderGeometry(radiusTop, radiusBottom, height, radialSegments);
            }
            case 'cone': {
                const { radius, height, radialSegments = 32 } = primitive.parameters;
                return new THREE.ConeGeometry(radius, height, radialSegments);
            }
            case 'torus': {
                const { radius, tube, radialSegments = 16, tubularSegments = 48 } = primitive.parameters;
                return new THREE.TorusGeometry(radius, tube, radialSegments, tubularSegments);
            }
            default:
                console.warn(`Unknown primitive shape: ${primitive.shape}`);
                return new THREE.BoxGeometry(1, 1, 1);
        }
    }
    createMaterial(mat) {
        switch (mat.type) {
            case 'standard': {
                const material = new THREE.MeshStandardMaterial({
                    color: new THREE.Color(mat.color),
                    roughness: mat.roughness ?? 0.5,
                    metalness: mat.metalness ?? 0.0,
                    opacity: mat.opacity ?? 1.0,
                    transparent: mat.transparent ?? false,
                });
                if (mat.emissive) {
                    material.emissive = new THREE.Color(mat.emissive);
                    material.emissiveIntensity = mat.emissiveIntensity ?? 1.0;
                }
                // Color textures use sRGB (false), data textures use Linear (true)
                if (mat.map) {
                    material.map = this.loadTexture(mat.map, false);
                }
                if (mat.normalMap) {
                    material.normalMap = this.loadTexture(mat.normalMap, true);
                }
                if (mat.roughnessMap) {
                    material.roughnessMap = this.loadTexture(mat.roughnessMap, true);
                }
                if (mat.metalnessMap) {
                    material.metalnessMap = this.loadTexture(mat.metalnessMap, true);
                }
                if (mat.aoMap) {
                    material.aoMap = this.loadTexture(mat.aoMap, true);
                }
                return material;
            }
            case 'basic': {
                const material = new THREE.MeshBasicMaterial({
                    color: new THREE.Color(mat.color),
                    opacity: mat.opacity ?? 1.0,
                    transparent: mat.transparent ?? false,
                });
                if (mat.map) {
                    material.map = this.loadTexture(mat.map, false);
                }
                return material;
            }
            case 'phong': {
                return new THREE.MeshPhongMaterial({
                    color: new THREE.Color(mat.color),
                    specular: mat.specular ? new THREE.Color(mat.specular) : undefined,
                    shininess: mat.shininess ?? 30,
                    opacity: mat.opacity ?? 1.0,
                    transparent: mat.transparent ?? false,
                });
            }
            default:
                console.warn(`Unknown material type: ${mat.type}`);
                return new THREE.MeshStandardMaterial({ color: 0x888888 });
        }
    }
    /**
     * Load a texture from a reference
     * @param texRef - Texture reference with URL and settings
     * @param isDataTexture - True for normal/roughness/metalness/ao maps (Linear color space),
     *                        False for diffuse/emissive maps (sRGB color space)
     */
    loadTexture(texRef, isDataTexture = false) {
        // Skip empty URLs
        if (!texRef.url) {
            console.warn('[SceneLoader] Texture reference has empty URL, skipping');
            return new THREE.Texture();
        }
        // Use different cache keys for different color space interpretations
        const cacheKey = `${texRef.url.substring(0, 50)}:${isDataTexture ? 'linear' : 'srgb'}`;
        // Check cache first
        if (this.textureCache.has(cacheKey)) {
            return this.textureCache.get(cacheKey);
        }
        // Resolve relative URLs with baseUrl (data URLs pass through unchanged)
        const resolvedUrl = this.resolveUrl(texRef.url);
        const texture = this.textureLoader.load(resolvedUrl);
        // Set color space for correct color rendering
        // - Color textures (diffuse, emissive) should use sRGB
        // - Data textures (normal, roughness, metalness, ao) should use Linear
        texture.colorSpace = isDataTexture ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
        if (texRef.repeat) {
            texture.repeat.set(texRef.repeat[0], texRef.repeat[1]);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
        }
        if (texRef.offset) {
            texture.offset.set(texRef.offset[0], texRef.offset[1]);
        }
        if (texRef.rotation !== undefined) {
            texture.rotation = texRef.rotation;
        }
        // Apply anisotropic filtering to prevent moiré patterns
        texture.anisotropy = this.maxAnisotropy;
        this.textureCache.set(cacheKey, texture);
        return texture;
    }
    async createMesh(obj) {
        const group = new THREE.Group();
        group.name = obj.name;
        group.userData.sdlId = obj.id;
        group.userData.sdlType = 'mesh';
        group.userData.meshUrl = obj.meshUrl;
        group.userData.locked = obj.locked;
        group.position.set(...obj.position);
        group.rotation.set(...obj.rotation);
        group.scale.set(...obj.scale);
        group.visible = obj.visible;
        if (!this.gltfLoader || !obj.meshUrl) {
            console.warn(`GLTFLoader not provided or meshUrl missing for mesh: ${obj.name}`);
            const placeholder = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff00ff, wireframe: true }));
            placeholder.name = 'placeholder';
            group.add(placeholder);
            return group;
        }
        const loader = this.gltfLoader;
        const meshUrl = this.resolveUrl(obj.meshUrl);
        try {
            let gltf;
            // Handle data URLs specially - they need to be parsed, not fetched
            if (obj.meshUrl.startsWith('data:')) {
                gltf = await new Promise((resolve, reject) => {
                    const arrayBuffer = this.dataUrlToArrayBuffer(obj.meshUrl);
                    loader.parse(arrayBuffer, '', (result) => resolve(result), (err) => reject(err));
                });
            }
            else {
                gltf = await loader.loadAsync(meshUrl);
            }
            const root = gltf.scene;
            root.name = obj.name;
            root.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.castShadow = obj.castShadow ?? true;
                    child.receiveShadow = obj.receiveShadow ?? true;
                    if (obj.material) {
                        child.material = this.createMaterial(obj.material);
                    }
                    // Ensure GLB materials are properly configured for shadows
                    // GLB materials may have settings that interfere with shadow receiving on some platforms
                    const materials = Array.isArray(child.material) ? child.material : [child.material];
                    for (const mat of materials) {
                        // MeshPhysicalMaterial extends MeshStandardMaterial
                        if (mat instanceof THREE.MeshStandardMaterial) {
                            // Force FrontSide - DoubleSide causes rendering issues on Adreno GPUs
                            // See: https://github.com/mrdoob/three.js/issues/15850
                            if (mat.side === THREE.DoubleSide) {
                                mat.side = THREE.FrontSide;
                            }
                            // Ensure depth testing is enabled (required for shadows)
                            mat.depthTest = true;
                            mat.depthWrite = true;
                            // Trigger shader recompilation with shadow support
                            mat.needsUpdate = true;
                            // Apply anisotropic filtering to all textures
                            const textureProps = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'bumpMap'];
                            for (const prop of textureProps) {
                                const texture = mat[prop];
                                if (texture) {
                                    texture.anisotropy = this.maxAnisotropy;
                                }
                            }
                        }
                        else if (mat instanceof THREE.MeshBasicMaterial || mat instanceof THREE.MeshPhongMaterial) {
                            // Apply anisotropic filtering to textures
                            const textureProps = ['map', 'normalMap', 'specularMap', 'emissiveMap', 'bumpMap'];
                            for (const prop of textureProps) {
                                const texture = mat[prop];
                                if (texture) {
                                    texture.anisotropy = this.maxAnisotropy;
                                }
                            }
                            mat.needsUpdate = true;
                        }
                    }
                }
            });
            group.add(root);
            return group;
        }
        catch (err) {
            console.error(`Failed to load mesh: ${obj.name}`, err);
            const placeholder = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0xff00ff, wireframe: true }));
            placeholder.name = 'placeholder';
            group.add(placeholder);
            return group;
        }
    }
    /**
     * Convert a data URL to ArrayBuffer for GLTFLoader.parse()
     */
    dataUrlToArrayBuffer(dataUrl) {
        // Remove the data URL prefix (e.g., "data:model/gltf-binary;base64,")
        const base64Index = dataUrl.indexOf(',');
        if (base64Index === -1) {
            throw new Error('Invalid data URL format');
        }
        const base64 = dataUrl.substring(base64Index + 1);
        // Decode base64 to binary string
        const binaryString = atob(base64);
        // Convert to ArrayBuffer
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
    createGroup(obj) {
        const group = new THREE.Group();
        group.name = obj.name;
        group.userData.sdlId = obj.id;
        group.userData.sdlType = 'group';
        group.userData.locked = obj.locked;
        group.position.set(...obj.position);
        group.rotation.set(...obj.rotation);
        group.scale.set(...obj.scale);
        group.visible = obj.visible;
        return group;
    }
    /**
     * Create an invisible placeholder for collider objects.
     * Colliders are physics-only and invisible in game - the actual physics body
     * is created by GameScene using the physics data stored in physicsData map.
     */
    createCollider(obj) {
        const group = new THREE.Group();
        group.name = obj.name;
        group.userData.sdlId = obj.id;
        group.userData.sdlType = 'collider';
        group.userData.colliderShape = obj.colliderShape;
        group.userData.size = obj.size;
        group.position.set(...obj.position);
        group.rotation.set(...obj.rotation);
        group.scale.set(...obj.scale);
        group.visible = false; // Colliders are invisible in game
        return group;
    }
    createLight(light) {
        switch (light.type) {
            case 'ambient': {
                const ambientLight = new THREE.AmbientLight(new THREE.Color(light.color), light.intensity);
                ambientLight.name = light.name;
                ambientLight.userData.sdlId = light.id;
                ambientLight.visible = light.visible;
                if (light.anchors)
                    ambientLight.userData.sdlAnchors = light.anchors;
                return ambientLight;
            }
            case 'directional': {
                const directionalLight = new THREE.DirectionalLight(new THREE.Color(light.color), light.intensity);
                directionalLight.name = light.name;
                directionalLight.userData.sdlId = light.id;
                directionalLight.position.set(...light.position);
                if (light.target) {
                    directionalLight.target.position.set(...light.target);
                }
                directionalLight.castShadow = light.castShadow;
                if (light.castShadow) {
                    directionalLight.shadow.mapSize.width = light.shadowMapSize ?? 2048;
                    directionalLight.shadow.mapSize.height = light.shadowMapSize ?? 2048;
                    if (light.shadowBias !== undefined) {
                        directionalLight.shadow.bias = light.shadowBias;
                    }
                    directionalLight.shadow.normalBias = light.shadowNormalBias ?? 0;
                    if (light.shadowCameraNear !== undefined) {
                        directionalLight.shadow.camera.near = light.shadowCameraNear;
                    }
                    if (light.shadowCameraFar !== undefined) {
                        directionalLight.shadow.camera.far = light.shadowCameraFar;
                    }
                    if (light.shadowCameraSize !== undefined) {
                        const size = light.shadowCameraSize;
                        directionalLight.shadow.camera.left = -size;
                        directionalLight.shadow.camera.right = size;
                        directionalLight.shadow.camera.top = size;
                        directionalLight.shadow.camera.bottom = -size;
                    }
                }
                directionalLight.visible = light.visible;
                if (light.anchors)
                    directionalLight.userData.sdlAnchors = light.anchors;
                return directionalLight;
            }
            case 'point': {
                const pointLight = new THREE.PointLight(new THREE.Color(light.color), light.intensity, light.distance ?? 0, light.decay ?? 2);
                pointLight.name = light.name;
                pointLight.userData.sdlId = light.id;
                pointLight.position.set(...light.position);
                pointLight.castShadow = light.castShadow;
                if (light.castShadow && light.shadowMapSize) {
                    pointLight.shadow.mapSize.width = light.shadowMapSize;
                    pointLight.shadow.mapSize.height = light.shadowMapSize;
                }
                pointLight.visible = light.visible;
                if (light.anchors)
                    pointLight.userData.sdlAnchors = light.anchors;
                return pointLight;
            }
            case 'spot': {
                const spotLight = new THREE.SpotLight(new THREE.Color(light.color), light.intensity, light.distance ?? 0, light.angle ?? Math.PI / 6, light.penumbra ?? 0.5, // Default to soft edge
                light.decay ?? 2);
                spotLight.name = light.name;
                spotLight.userData.sdlId = light.id;
                spotLight.position.set(...light.position);
                if (light.target) {
                    spotLight.target.position.set(...light.target);
                }
                spotLight.castShadow = light.castShadow ?? false;
                if (spotLight.castShadow) {
                    spotLight.shadow.mapSize.width = light.shadowMapSize ?? 2048;
                    spotLight.shadow.mapSize.height = light.shadowMapSize ?? 2048;
                    spotLight.shadow.bias = light.shadowBias ?? 0;
                    spotLight.shadow.normalBias = light.shadowNormalBias ?? 0;
                }
                spotLight.visible = light.visible;
                if (light.anchors)
                    spotLight.userData.sdlAnchors = light.anchors;
                return spotLight;
            }
            default:
                console.warn(`Unknown light type: ${light.type}`);
                return null;
        }
    }
    loadDecals(decals, scene) {
        if (!decals || decals.length === 0)
            return;
        for (const decal of decals) {
            const target = this.findTargetMesh(scene, decal.targetObjectId);
            if (!target) {
                console.warn(`[SceneLoader] Decal target not found: ${decal.targetObjectId ?? 'unknown'}`);
                continue;
            }
            if (!decal.textureUrl) {
                console.warn(`[SceneLoader] Decal ${decal.name} missing textureUrl`);
                continue;
            }
            const texture = this.textureLoader.load(this.resolveUrl(decal.textureUrl));
            texture.colorSpace = THREE.SRGBColorSpace;
            const orientation = new THREE.Euler(...decal.orientation);
            if (decal.rotation) {
                orientation.z += decal.rotation;
            }
            const geometry = new DecalGeometry(target, new THREE.Vector3(...decal.position), orientation, new THREE.Vector3(...decal.size));
            const material = new THREE.MeshStandardMaterial({
                map: texture,
                transparent: true,
                opacity: decal.opacity ?? 1,
                depthWrite: false,
                polygonOffset: true,
                polygonOffsetFactor: -4,
                polygonOffsetUnits: -4,
            });
            const mesh = new THREE.Mesh(geometry, material);
            mesh.visible = decal.visible !== false;
            scene.add(mesh);
        }
    }
    findTargetMesh(scene, targetObjectId) {
        if (!targetObjectId)
            return null;
        // First, find the group/object with this sdlId
        let targetGroup = null;
        scene.traverse((child) => {
            if (targetGroup)
                return;
            if (child.userData?.sdlId === targetObjectId) {
                targetGroup = child;
            }
        });
        if (!targetGroup)
            return null;
        const group = targetGroup;
        // If the group itself is a mesh (primitive), return it directly
        if (group instanceof THREE.Mesh)
            return group;
        // Otherwise, find the first mesh child inside the group
        let target = null;
        group.traverse((child) => {
            if (target)
                return;
            if (child instanceof THREE.Mesh) {
                target = child;
            }
        });
        return target;
    }
    /**
     * Get collision group bitmask for a physics body
     */
    static getCollisionGroup(group) {
        return SDL_COLLISION_GROUPS[group] ?? SDL_COLLISION_GROUPS.DEFAULT;
    }
    /**
     * Get collision mask for a physics body
     */
    static getCollisionMask(group) {
        return SDL_COLLISION_MASKS[group] ?? SDL_COLLISION_MASKS.DEFAULT;
    }
    dispose() {
        this.textureCache.forEach(texture => texture.dispose());
        this.textureCache.clear();
    }
}
//# sourceMappingURL=SceneLoader.js.map